"use client";

import { useState, useEffect, useRef } from "react";
import Link from "next/link";
import { useAuth } from "../../lib/hooks/useAuth";
import { useSession } from "next-auth/react";
import { Button } from "../ui/Button";
import { useRouter } from "next/navigation";
import {
  User,
  LogOut,
  Package,
  Heart,
  CreditCard,
  ChevronDown,
  ChevronUp,
  MessageCircle,
} from "lucide-react";
import toast from "react-hot-toast";

export function ProfileDropdown() {
  const { user, loading, logout } = useAuth();
  const { data: session, status: sessionStatus } = useSession();
  const router = useRouter();

  // NextAuth 세션이 있으면 Firebase Auth 대신 사용
  const isAuthenticated = user || session?.user;
  const isLoading = loading || sessionStatus === "loading";
  const currentUser = user || session?.user;

  // 디버깅용 로그
  console.log(
    "ProfileDropdown - user:",
    user?.uid,
    "session:",
    session?.user?.email,
    "loading:",
    isLoading
  );

  const [isOpen, setIsOpen] = useState(false);
  const [isMyItemsOpen, setIsMyItemsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);



  // 외부 클릭 시 드롭다운 닫기
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleLogout = async () => {
    try {
      setIsOpen(false);

      // NextAuth 세션이 있으면 NextAuth 로그아웃 우선
      if (session) {
        console.log("🔄 NextAuth 로그아웃 시작");

        // Firebase Auth도 함께 로그아웃
        if (user) {
          console.log("🔄 Firebase Auth도 함께 로그아웃");
          await logout();
        }

        const { signOut } = await import("next-auth/react");
        await signOut({
          callbackUrl: "/",
          redirect: true,
        });

        // 브라우저 세션 완전 정리
        if (typeof window !== "undefined") {
          // 로컬 스토리지 정리
          localStorage.clear();
          // 세션 스토리지 정리
          sessionStorage.clear();
          // 쿠키 정리 (가능한 경우)
          document.cookie.split(";").forEach(cookie => {
            const eqPos = cookie.indexOf("=");
            const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
            document.cookie =
              name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/";
          });
        }

        console.log("✅ NextAuth 로그아웃 완료");
      } else if (user) {
        // Firebase Auth 로그아웃
        console.log("🔄 Firebase Auth 로그아웃 시작");
        await logout();
        console.log("✅ Firebase Auth 로그아웃 완료");
        router.push("/");
      }

      toast.success("로그아웃되었습니다.");
    } catch (error) {
      console.error("로그아웃 실패:", error);
      toast.error("로그아웃 중 오류가 발생했습니다.");
      // 로그아웃 실패해도 홈으로 이동
      router.push("/");
    }
  };


  if (loading) {
    return (
      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="flex items-center space-x-2">
        <Link href="/auth/login">
          <Button variant="ghost" size="sm">
            로그인
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="relative" ref={dropdownRef}>
      {/* 프로필 버튼 */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 hover:bg-gray-50 rounded-lg p-2 transition-colors"
      >
        <div className="w-8 h-8 rounded-full overflow-hidden bg-gray-200 flex items-center justify-center">
          {currentUser?.image || user?.profileImage ? (
            <img
              src={currentUser?.image || user?.profileImage}
              alt={currentUser?.name || user?.nickname}
              className="w-full h-full object-cover"
            />
          ) : (
            <span className="text-sm font-medium text-gray-600">
              {(currentUser?.name || user?.nickname)?.charAt(0)?.toUpperCase()}
            </span>
          )}
        </div>
        <div className="hidden sm:flex flex-col items-start">
          <span className="text-sm font-medium text-gray-700">
            {currentUser?.name || user?.nickname}
          </span>
        </div>
        <ChevronDown className="w-4 h-4 text-gray-500" />
      </button>

      {/* 드롭다운 메뉴 */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-72 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
          {/* 사용자 정보 */}
          <div className="px-4 py-3 border-b border-gray-100">
            <div className="flex items-center space-x-3 mb-3">
              <div className="w-10 h-10 rounded-full overflow-hidden bg-gray-200 flex items-center justify-center">
                {currentUser?.image || user?.profileImage ? (
                  <img
                    src={currentUser?.image || user?.profileImage}
                    alt={currentUser?.name || user?.nickname}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-lg font-medium text-gray-600">
                    {(currentUser?.name || user?.nickname)
                      ?.charAt(0)
                      ?.toUpperCase()}
                  </span>
                )}
              </div>
              <div className="flex-1">
                <div className="text-sm font-medium text-gray-900">
                  {currentUser?.name || user?.nickname}
                </div>
                {/* 이메일 제거됨 */}
              </div>
            </div>
          </div>

          {/* 프로필 보기 */}
          <div className="py-1 border-b border-gray-100">
            <Link
              href="/mypage"
              className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
              onClick={() => setIsOpen(false)}
            >
              <User className="w-4 h-4 mr-3" />
              프로필
            </Link>

            {/* 채팅 */}
            <Link
              href="/chat"
              className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
              onClick={() => setIsOpen(false)}
            >
              <MessageCircle className="w-4 h-4 mr-3" />
              채팅
            </Link>
          </div>

          {/* 내 상품 관리 섹션 (하위 메뉴) */}
          <div className="py-1 border-b border-gray-100">
            <button
              onClick={() => setIsMyItemsOpen(!isMyItemsOpen)}
              className="flex items-center justify-between w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              <div className="flex items-center">
                <Package className="w-4 h-4 mr-3" />내 상품 관리
              </div>
              {isMyItemsOpen ? (
                <ChevronUp className="w-4 h-4" />
              ) : (
                <ChevronDown className="w-4 h-4" />
              )}
            </button>

            {/* 하위 메뉴 */}
            {isMyItemsOpen && (
              <div className="pl-4 bg-gray-50">
                <Link
                  href="/profile/items"
                  className="flex items-center px-4 py-2 text-sm text-gray-600 hover:bg-gray-100"
                  onClick={() => setIsOpen(false)}
                >
                  <Package className="w-4 h-4 mr-3" />내 상품
                </Link>

                <Link
                  href="/profile/wishlist"
                  className="flex items-center px-4 py-2 text-sm text-gray-600 hover:bg-gray-100"
                  onClick={() => setIsOpen(false)}
                >
                  <Heart className="w-4 h-4 mr-3" />
                  찜한 상품
                </Link>
              </div>
            )}
          </div>

          {/* 구분선 */}
          <div className="border-t border-gray-100 my-1"></div>

          {/* 로그아웃 */}
          <button
            onClick={handleLogout}
            className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
          >
            <LogOut className="w-4 h-4 mr-3" />
            로그아웃
          </button>
        </div>
      )}
    </div>
  );
}
